import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { AlertTriangle } from "react-feather";
import {
  programService,
  type RemovalOutstandingAffiliate,
} from "@/services/program.service";
import { payoutService } from "@/services/payout.service";
import { formatCurrency } from "@/utils/currency";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type RemoveAffiliatesModalProps = {
  open: boolean;
  programId: string;
  /** Affiliate ids about to be removed (one for the row action, many for bulk). */
  affiliateIds: string[];
  /** "Jane Doe" or "12 affiliates" — drives the confirm wording. */
  subject: string;
  isRemoving: boolean;
  onClose: () => void;
  /** Called with the ids to actually remove (may be a subset when some are skipped). */
  onConfirm: (affiliateIds: string[]) => void;
};

function displayName(a: {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
}) {
  return `${a.firstName ?? ""} ${a.lastName ?? ""}`.trim() || a.email;
}

/**
 * The removal confirm dialog, with the money question in front of it.
 *
 * Opening it runs a preflight: while any of the affiliates still has a
 * PENDING payout the backend refuses the removal (409), so this shows those
 * payouts and lets the brand pay them right here through the same contract
 * the payouts page uses. Once nothing is owed it becomes a plain confirm.
 */
export function RemoveAffiliatesModal({
  open,
  programId,
  affiliateIds,
  subject,
  isRemoving,
  onClose,
  onConfirm,
}: RemoveAffiliatesModalProps) {
  const queryClient = useQueryClient();
  const idsKey = useMemo(() => [...affiliateIds].sort().join(","), [affiliateIds]);

  // A fresh preflight on every open — a payout may have been requested since
  // the last time this dialog was up.
  const [openNonce, setOpenNonce] = useState(0);
  const [methodByPayoutId, setMethodByPayoutId] = useState<
    Record<string, string>
  >({});
  useEffect(() => {
    if (open) {
      setOpenNonce((n) => n + 1);
      setMethodByPayoutId({});
    }
  }, [open]);

  const preflight = useQuery({
    queryKey: ["removal-preflight", programId, idsKey, openNonce],
    queryFn: () =>
      programService.getAffiliateRemovalPreflight(programId, affiliateIds),
    enabled: open && openNonce > 0 && affiliateIds.length > 0,
    staleTime: 0,
  });

  const outstanding = useMemo<RemovalOutstandingAffiliate[]>(
    () => preflight.data?.outstanding ?? [],
    [preflight.data],
  );
  const blocked = !!preflight.data?.blocked;
  const blockedIds = useMemo(
    () => new Set(outstanding.map((o) => o.affiliateId)),
    [outstanding],
  );
  const removableIds = useMemo(
    () => affiliateIds.filter((id) => !blockedIds.has(id)),
    [affiliateIds, blockedIds],
  );

  // The method in effect for a payout: the brand's pick, else the first one
  // both sides support. Payouts with no shared method cannot be paid here.
  const chosenMethod = (payout: RemovalOutstandingAffiliate["payouts"][number]) =>
    methodByPayoutId[payout.id] ?? payout.matchingPaymentInfos[0]?.paymentMethod.id;

  const allPayouts = outstanding.flatMap((o) => o.payouts);
  const settleable = allPayouts.filter(
    (p) => p.matchingPaymentInfos.length > 0,
  );
  const unsettleableCount = allPayouts.length - settleable.length;
  const settleTotal = settleable.reduce((acc, p) => acc + p.amount, 0);

  const settleMutation = useMutation({
    mutationFn: () =>
      payoutService.bulkPayout(
        settleable.map((p) => ({
          payoutId: p.id,
          paymentMethodId: chosenMethod(p)!,
        })),
      ),
    onSuccess: async () => {
      toast.success(
        `Paid ${formatCurrency(settleTotal)} across ${settleable.length} payout${
          settleable.length === 1 ? "" : "s"
        }`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["removal-preflight", programId],
      });
    },
    onError: (err: unknown) => {
      const message = (err as AxiosError<{ message?: string }>)?.response?.data
        ?.message;
      toast.error(message || "Failed to pay the outstanding payouts");
    },
  });

  const busy = isRemoving || settleMutation.isPending;
  const plural = affiliateIds.length !== 1;
  const unrequested = preflight.data?.unrequestedBalances ?? [];

  const confirmMessage = plural
    ? `Remove ${subject} from this program? This only affects this program — their Reveshare accounts and any other programs they're in stay untouched. The discount codes and tracking links they use for this program will stop working. Past sales and payout records are kept, and they can rejoin later, though their stats for this program will start fresh.`
    : `Remove ${subject} from this program? This only affects this program — their Reveshare account and any other programs they're in stay untouched. The discount code and tracking link they use for this program will stop working. Past sales and payout records are kept, and they can rejoin later, though their stats for this program will start fresh.`;

  const unrequestedMessage =
    unrequested.length === 0
      ? null
      : unrequested.length === 1 && !plural
        ? `${displayName(unrequested[0].affiliate)} has earned ${formatCurrency(
            unrequested[0].amount,
          )} in commissions they haven't requested yet. That balance can't be paid out once they're removed.`
        : `${unrequested.length} of these affiliates ${
            unrequested.length === 1 ? "has" : "have"
          } earned commissions they haven't requested yet (${formatCurrency(
            preflight.data?.totalUnrequested ?? 0,
          )} in total). Those balances can't be paid out once they're removed.`;

  const blockedMessage =
    outstanding.length === 1
      ? `${displayName(outstanding[0].affiliate)} still has ${
          outstanding[0].payouts.length === 1
            ? "a pending payout request"
            : `${outstanding[0].payouts.length} pending payout requests`
        } totalling ${formatCurrency(
          preflight.data?.totalOutstanding ?? 0,
        )}. Pay it here first — removing now would leave that money unpayable.`
      : `${outstanding.length} of these affiliates still have pending payout requests totalling ${formatCurrency(
          preflight.data?.totalOutstanding ?? 0,
        )}. Pay them here first — removing now would leave that money unpayable.`;

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
      panelClassName={blocked ? "max-w-2xl" : undefined}
    >
      <DialogContent>
        <DialogHeader showCloseButton onClose={() => !busy && onClose()}>
          <span className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </span>
            Remove from Program
          </span>
        </DialogHeader>

        {preflight.isPending || !preflight.data ? (
          preflight.isError ? (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-3">
                <span>Couldn't check for outstanding payouts.</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => preflight.refetch()}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : (
            <div className="flex items-center gap-3 py-2 text-sm text-muted-foreground">
              <Spinner size="sm" />
              Checking for outstanding payouts…
            </div>
          )
        ) : blocked ? (
          <>
            <Alert variant="warning">
              <AlertDescription>{blockedMessage}</AlertDescription>
            </Alert>

            <div className="mt-4 max-h-[50vh] space-y-4 overflow-y-auto pr-1">
              {outstanding.map((group) => (
                <div
                  key={group.affiliateId}
                  className="rounded-lg border border-border"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-muted/40 px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {displayName(group.affiliate)}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {group.affiliate.email}
                        {group.code ? ` · ${group.code}` : ""}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-foreground">
                      {formatCurrency(group.total)}
                    </span>
                  </div>
                  <ul className="divide-y divide-border">
                    {group.payouts.map((payout) => {
                      const infos = payout.matchingPaymentInfos;
                      const selected = chosenMethod(payout);
                      return (
                        <li
                          key={payout.id}
                          className="flex flex-wrap items-center gap-3 px-3 py-2"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground">
                              {formatCurrency(payout.amount)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Requested{" "}
                              {new Date(payout.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="w-full sm:w-48">
                            {infos.length === 0 ? (
                              <p className="text-xs text-destructive">
                                No payment method you both support — pay this
                                one from the Payouts page once there is one.
                              </p>
                            ) : (
                              <Select
                                value={selected ?? ""}
                                disabled={busy}
                                onValueChange={(paymentMethodId) =>
                                  setMethodByPayoutId((prev) => ({
                                    ...prev,
                                    [payout.id]: paymentMethodId,
                                  }))
                                }
                              >
                                <SelectTrigger className="capitalize bg-card">
                                  <SelectValue placeholder="Select method" />
                                </SelectTrigger>
                                <SelectContent>
                                  {infos.map((info) => (
                                    <SelectItem
                                      key={info.paymentMethod.id}
                                      value={info.paymentMethod.id}
                                      className="capitalize"
                                    >
                                      {info.paymentMethod.name.toLowerCase()}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            {unsettleableCount > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">
                {unsettleableCount} payout{unsettleableCount === 1 ? "" : "s"}{" "}
                can't be paid from here. Those affiliates stay in the program
                until it's settled.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{confirmMessage}</p>
            {unrequestedMessage && (
              <Alert variant="warning" className="mt-3">
                <AlertDescription>{unrequestedMessage}</AlertDescription>
              </Alert>
            )}
          </>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          {preflight.data && blocked ? (
            <>
              {removableIds.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onConfirm(removableIds)}
                  disabled={busy}
                >
                  {isRemoving && <Spinner size="sm" className="border-current" />}
                  Remove the {removableIds.length} without payouts
                </Button>
              )}
              <Button
                type="button"
                onClick={() => settleMutation.mutate()}
                disabled={busy || settleable.length === 0}
              >
                {settleMutation.isPending && (
                  <Spinner size="sm" className="border-current" />
                )}
                {settleMutation.isPending
                  ? "Paying..."
                  : `Pay ${formatCurrency(settleTotal)}`}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="destructive"
              onClick={() => onConfirm(affiliateIds)}
              disabled={busy || !preflight.data}
            >
              {isRemoving && <Spinner size="sm" className="border-current" />}
              {isRemoving ? "Remove..." : "Remove"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
